import { useTranslation } from "react-i18next";
import {
  Button,
  chakra,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useToast,
} from "@chakra-ui/react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { nonNumericSymbols, showErrorToast, sortAlphabeticallyByAttribute } from "../../../../utils";
import { FormSelect, NumericFormInput } from "../../../../components/Forms";
import * as React from "react";
import { hideAppSpinner } from "../../../../context/Store/slices/appSpinnerSlice";
import { useGetAllSuppliers, useGetCountries, useGetProductCategoriesPerLocation } from "../../../../hooks/query";
import { useDispatch } from "react-redux";
import { spendAmountsYearsKeys } from "./ManufacturingOrSourcingLocations";

function SourcingLocationModal({ modalData, isOpen, onClose, onSubmit }) {
  const { t } = useTranslation("translation", {
    keyPrefix: "supplierManagement.manufacturingOrSourcingLocations.modal",
  });
  const { t: globalTranslation } = useTranslation("translation");
  const toast = useToast();
  const dispatch = useDispatch();

  const [suppliers, setSuppliers] = useState([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: modalData,
  });

  const { isFetching: isFetchingSuppliers } = useGetAllSuppliers({
    onSuccess: (data) => {
      setSuppliers(data.sort((a, b) => sortAlphabeticallyByAttribute(a, b, "name")));
    },
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner()),
    cacheTime: 0,
  });

  const { data: countries, isFetching: isFetchingCountries } = useGetCountries({
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner()),
    cacheTime: 0,
  });

  const { data: productCodes, isFetching: isFetchingCodes } = useGetProductCategoriesPerLocation(
    { location: "UK" },
    {
      onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    }
  );

  const prepareSubmitData = (data) => {
    onSubmit(modalData?.id, {
      supplier: data.supplier,
      location: data.location,
      industryCode: data.industryCode,
      spentAmounts: spendAmountsYearsKeys.map((yearKey) => {
        return {
          id: modalData?.spentAmounts?.find((el) => el.year === yearKey)?.id ?? undefined,
          year: yearKey,
          amount: data[`amountSpend${yearKey}`],
        };
      }),
    });
  };

  return isOpen && !isFetchingSuppliers && !isFetchingCountries && !isFetchingCodes ? (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <chakra.form onSubmit={handleSubmit(prepareSubmitData)}>
          <ModalHeader>{modalData ? t("updateSupplierLocationHeader") : t("createSupplierLocationHeader")}</ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <Flex direction="column" gap="2" marginBottom={10}>
              <FormSelect
                label={`${t("labels.supplier")} *`}
                placeholder={" "}
                errors={errors}
                {...register("supplier", {
                  required: globalTranslation("entityRequired", { entityName: t("labels.supplier") }),
                })}
                maxInlineSize={"var(--chakra-sizes-full)"}
              >
                {suppliers.map((supplier) => (
                  <option key={`supplier-option-${supplier.id}`} value={supplier.name}>
                    {supplier.name}
                  </option>
                ))}
              </FormSelect>
              <FormSelect
                label={`${t("labels.location")}`}
                placeholder={" "}
                errors={errors}
                {...register("location")}
                maxInlineSize={"var(--chakra-sizes-full)"}
              >
                {countries.map((country) => (
                  <option key={`country-option-${country.id}`} value={country.name}>
                    {country.name}
                  </option>
                ))}
              </FormSelect>
              <FormSelect
                label={`${t("labels.industryCode")}`}
                placeholder={" "}
                errors={errors}
                {...register("industryCode")}
                maxInlineSize={"var(--chakra-sizes-full)"}
              >
                {productCodes?.map(
                  (el) =>
                    (
                      <option key={`${el.id}-${el.name}`} value={el.code}>
                        {el.code} - {el.name}
                      </option>
                    ) ?? []
                ) ?? []}
              </FormSelect>
              {spendAmountsYearsKeys.map((yearKey) => {
                return (
                  <NumericFormInput
                    key={`amountSpendInput-${yearKey}`}
                    label={`${t("labels.amountSpend", { year: yearKey })}`}
                    errors={errors}
                    {...register(`amountSpend${yearKey}`)}
                    onKeyDown={(e) => nonNumericSymbols.includes(e.key) && e.preventDefault()}
                    maxInlineSize={"var(--chakra-sizes-full)"}
                  />
                );
              })}
            </Flex>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              {t("buttons.close")}
            </Button>
            <Button colorScheme="customScheme" type="submit">
              {t("buttons.save")}
            </Button>
          </ModalFooter>
        </chakra.form>
      </ModalContent>
    </Modal>
  ) : null;
}

export default SourcingLocationModal;
